import { Injectable } from '@angular/core';
import { TopicService } from './topic.service';

@Injectable({
  providedIn: 'root'
})
export class CompressionService {

  constructor(topicService: TopicService) { }

  /** decompress a text compressed with Word-Compression */
  decompress(compressedText: string): string {
    let text = compressedText;
    let words = text.split(" ");
    let newWords = [];
    for (let i = 0; i < words.length; i++) {
      let pieces = words[i].split(/([#+])/);
      let word = "";
      let lastSign = "";
      for (let p of pieces) {
        if (p.charAt(0) == "+" || p.charAt(0) == "#") {
          lastSign = p;
        } else {
          if (lastSign != "") {
            let number = "";
            for (let i = 0; i < p.length; i++) {
              if (!Number.isNaN(+p.charAt(i))) {
                number = number.concat(p.charAt(i));
              }
            }
            if (lastSign == "#") {
              word = word.concat(newWords[+number - 1]);
            } else {
              word = word.concat(newWords[i - +number]);
            }
            word = word.concat(p.substr(number.length, p.length - number.length));

          }
          if (lastSign == "") {
            word = word.concat(p);
          }
          lastSign = "";
        }

      }
      newWords.push(word);
    }

    text = newWords.join(" ");
    return text;
  }

  /** Compress a Text using word-compression */
  compress(text: string, absolute: boolean, relative: boolean): string {
    let compressedText = "";

    let words = text.split(" ");

    let i = 0;
    for (let word of words) {
      let previousWords = words.slice(0, i);

      let shortestRepresentation = this.shortestWordRepr(word, previousWords, absolute, relative);

      compressedText = compressedText.concat(" ", shortestRepresentation);
      i++;
    }

    compressedText = compressedText.trim();
    return compressedText;

  }

  /** get shortest representation of the given word based on the previous words */
  shortestWordRepr(word: string, previousWords: string[], absolute, relative): string {

    let i = previousWords.length;

    let shortestRepresentation = word;

    for (let pw of previousWords) {
      if (word.includes(pw)) {
        let placeholder = ""

        if (absolute && !relative) {
          placeholder = "#" + (previousWords.indexOf(pw) + 1);
        } else if (!absolute && relative) {
          placeholder = "+" + (i - previousWords.lastIndexOf(pw));
        } else if (absolute && relative) {
          if ((previousWords.indexOf(pw) + 1) < (i - previousWords.lastIndexOf(pw))) {
            placeholder = "#" + (previousWords.indexOf(pw) + 1);
          } else {
            placeholder = "+" + (i - previousWords.lastIndexOf(pw));
          }
        }
        let start = word.indexOf(pw);
        let end = start + pw.length;
        let temp_word = word.slice(0, start).concat(placeholder, word.slice(end, word.length));
        let repr = this.shortestWordRepr(temp_word, previousWords, absolute, relative);

        if (repr.length < shortestRepresentation.length) {
          shortestRepresentation = repr;
        }
      }
    }

    return shortestRepresentation;
  }

  /** compress text based on letter compression */
  compressLetter(text: string, mapping: string[][]): string {
    let compressedText = "";

    let map0 = mapping.map(v => v[0]);

    for (let c of text) {
      let index = map0.indexOf(c);
      compressedText = compressedText.concat(mapping[index][1]);
    }
    return compressedText;
  }

  /** decompress text based on letter compression */
  decompressLetter(compressedText: string, mapping: string[][]): string {

    if(mapping.map(v => v[1]).filter(v => v=="" || v ==undefined).length > 0){
      return "";
    }

    if(compressedText == "" || compressedText == undefined){
      return "";
    }

    let text = "";

    let temp_text = "";
    for (let m of mapping) {
      if (compressedText.startsWith(m[1])) {
        let rest = compressedText.substr(m[1].length, compressedText.length);
        if (rest.length > 0) {
          let compressedRest = this.decompressLetter(rest, mapping);
          if (compressedRest != "") {
            temp_text = m[0].concat(compressedRest);
            if (temp_text.length > text.length || text == "") {
              text = temp_text;
            }
          }
        } else {
          text = m[0];
        }
      }
    }
    return text;
  }

  /** compute the letter compression tree */
  computeTree(mapping: string[][]): any {
    let graph: any = {};
    graph.nodes = [];
    graph.edges = [];

    let nodeList = [];
    for (let w of mapping) {
      let word = w[1];
      for (let i = 1; i < word.length + 1; i++) {
        nodeList.push(word.substr(0, i));
      }
    }
    nodeList = nodeList.filter((v, i) => nodeList.indexOf(v) == i);

    let map1 = mapping.map(v => v[1]);
    for (let n of nodeList) {
      let index = map1.indexOf(n);
      let label = n;
      if (index >= 0) {
        label = n.concat(" → ", mapping[index][0]);
      }
      graph.nodes.push({
        id: n,
        label: label
      });
    }

    graph.nodes.push({
      id: "start",
      label: "start"
    })

    for (let i = 0; i < nodeList.length; i++) {
      for (let j = i + 1; j < nodeList.length; j++) {
        if (nodeList[i].concat("0") == nodeList[j]) {
          graph.edges.push({
            source: nodeList[i],
            target: nodeList[j],
            label: ''
          });
        }
        if (nodeList[i].concat("1") == nodeList[j]) {
          graph.edges.push({
            source: nodeList[i],
            target: nodeList[j],
            label: ''
          });
        }
      }
    }

    graph.edges.push({
      source: "start",
      target: "1",
      label: ''
    });

    graph.edges.push({
      source: "start",
      target: "0",
      label: ''
    });

    return graph;

  }

  /** compute the tree for compression creation */
  computeTreeBySize(mapping: string[][]): any {
    let graph: any = {};
    graph.nodes = [];
    graph.edges = [];


    let min = Math.min(...mapping.map(v => +v[1]).filter(v => v > 0));
    min = 100 / (min);
    let size = Math.ceil(Math.log2(min));
    console.log(size);

    let length = 0;
    let nodeList = [""];
    let tempNodeList = [];
    while (length <= size) {
      for (let n of tempNodeList) {
        nodeList.push(n.concat("0"));
        nodeList.push(n.concat("1"));
      }
      tempNodeList = [...nodeList];
      length++;
    }

    nodeList.splice(nodeList.indexOf(""), 1);
    nodeList = nodeList.filter((v,i) => nodeList.indexOf(v)==i);

    console.log(nodeList);

    graph.nodes.push({
      id: "start",
      label: "start"
    });

    for (let n of nodeList) {
      if (n != "") {
        graph.nodes.push({
          id: n,
          label: n
        });
      }
    }

    for (let i = 0; i < nodeList.length; i++) {
      for (let j = i + 1; j < nodeList.length; j++) {
        
        if (nodeList[i].concat("1") == nodeList[j]) {
          graph.edges.push({
            source: nodeList[i],
            target: nodeList[j],
            label: ''
          });
        }
        if (nodeList[i].concat("0") == nodeList[j]) {
          graph.edges.push({
            source: nodeList[i],
            target: nodeList[j],
            label: ''
          });
        }
      }
    }


    graph.edges.push({
      source: "start",
      target: "0",
      label: ''
    });

    graph.edges.push({
      source: "start",
      target: "1",
      label: ''
    });

    return graph;
  }

  /** returns enough codes the given number of codes required */
  getCodes(countCodes: number): string[]{
    let totalLength = Math.ceil(Math.log2(countCodes+1));

    let length = 0;
    let codeList = [""];
    let tempCodeList = [];
    while (length <= totalLength) {
      for (let n of tempCodeList) {
        codeList.push(n.concat("0"));
        codeList.push(n.concat("1"));
      }
      tempCodeList = [...codeList];
      length++;
    }

    codeList.splice(codeList.indexOf(""), 1);
    codeList = codeList.filter((v,i) => codeList.indexOf(v)==i);
    return codeList;
  }
}
